import { urlFor } from "@/utils/client";
import { ISectors } from "@/utils/types";
import "keen-slider/keen-slider.min.css";
import { useKeenSlider } from "keen-slider/react";
import Image from "next/image";
import { useEffect, useState } from "react";
import { Button } from "./button";
import { SliderArrows } from "./sliderArrows";

export function Sectors(props: ISectors) {
  const sectors = props.sectors ?? [];
  const [currentSlide, setCurrentSlide] = useState(0);

  const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>({
    loop: sectors.length > 1,
    breakpoints: {
      "(max-width: 768px)": {
        slides: {
          perView: sectors.length > 1 ? 1.15 : 1,
          spacing: 16,
          origin: "center",
        },
      },
    },
    slides: {
      perView: Math.min(sectors.length || 1, 3.4),
      spacing: 30,
      origin: "center",
    },
    slideChanged(s) {
      setCurrentSlide(s.track.details.rel ?? 0);
    },
  });

  useEffect(() => {
    if (sectors.length === 0) return;
    setCurrentSlide(instanceRef.current?.track?.details?.rel ?? 0);
  }, [instanceRef, sectors.length]);

  if (sectors.length === 0) return null;

  return (
    <div className="flex flex-col items-center component-gap w-full overflow-hidden text-center">
      <div
        style={{
          color: props.accentColor ? props.accentColor : "#000",
        }}
        className="flex flex-col component-gap max-w-5xl px-4"
      >
        <p className="heading">{props.title}</p>
        {props.description && <p className="opacity-80">{props.description}</p>}
      </div>
      <div ref={sliderRef} className="keen-slider w-full">
        {sectors.map((sector, i) => {
          return (
            <div
              key={i}
              onClick={() => {
                if (!instanceRef.current) return;
                if (i === currentSlide) return;
                const total = sectors.length;
                const forwardDist = (i - currentSlide + total) % total;
                const backwardDist = (currentSlide - i + total) % total;
                if (forwardDist <= backwardDist) {
                  instanceRef.current.next();
                } else {
                  instanceRef.current.prev();
                }
              }}
              className="keen-slider__slide cursor-pointer"
            >
              <div
                className={`p-8 bg-white gap-8 flex flex-col items-start h-full transition-all duration-300 origin-center ${
                  currentSlide !== i
                    ? "scale-[0.97] opacity-75"
                    : "scale-100 opacity-100"
                }`}
              >
                {sector.image?.image && (
                  <Image
                    className="aspect-[16/9] w-full object-cover object-top"
                    width={640}
                    height={360}
                    sizes="(max-width: 768px) 100vw, 33vw"
                    alt={sector.image?.caption ?? ""}
                    src={urlFor(sector.image.image).url()}
                  />
                )}
                <div className="flex flex-col gap-2 items-start">
                  <p
                    style={{
                      color: props.accentColor ? props.accentColor : "#000",
                    }}
                    className="font-display text-2xl"
                  >
                    {sector.title}
                  </p>
                  {sector.description && (
                    <p
                      style={{
                        opacity: "75%",
                        color: props.accentColor ? props.accentColor : "#000",
                      }}
                      className="font-body text-left"
                    >
                      {sector.description}
                    </p>
                  )}
                </div>
                {sector.items?.length > 0 && (
                  <div className="flex flex-col gap-2 text-left w-full">
                    {sector.items.map((item, j) => (
                      <div
                        key={j}
                        className="flex gap-3 items-center border-b border-black/10 pb-2"
                      >
                        <span
                          style={{
                            color: props.accentColor
                              ? props.accentColor
                              : "#000",
                          }}
                          className="font-display text-sm w-6 h-6 flex items-center justify-center rounded-full border border-black/10 shrink-0"
                        >
                          {j + 1}
                        </span>
                        <p className="text-sm text-black/75">{item}</p>
                      </div>
                    ))}
                  </div>
                )}
                {sector.buttons && sector.buttons.length > 0 && (
                  <div className="flex gap-4">
                    {sector.buttons.map((button, j) => (
                      <Button key={j} link={button.link} label={button.label} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <SliderArrows
        onPrev={() => instanceRef.current?.prev()}
        onNext={() => instanceRef.current?.next()}
      />
    </div>
  );
}
